import { IProduceJoinDto } from "../../dto";
import {
  BulkProduceTypeEnum,
  DirectionEnum,
  ProductionTypeEnum,
} from "../../enums";
import { ICoursesCalendarSlotDto } from "../../models";
import { convertUTCToISO, dateToComparableNumber } from "../date.helpers";

/** Slots selected for bulk produce (no ride yet). */
const coursesCalendarSlotsToProduce = (
  slots: ICoursesCalendarSlotDto[],
): ICoursesCalendarSlotDto[] => slots.filter(slot => !slot.hasRide);

/**
 * Build shared production preview / produce payload from courses calendar selection.
 * Passes exact schedule occurrences so produce preview matches selected slots
 * (course + direction + shiftTime), not only course ids in a date range.
 */
export const buildCoursesCalendarBulkProduceJoinDto = (
  slots: ICoursesCalendarSlotDto[],
): IProduceJoinDto => {
  const slotsToProduce = coursesCalendarSlotsToProduce(slots);
  const courseIds = [...new Set(slotsToProduce.map(slot => slot.courseId))];
  const bookingIds = [
    ...new Set(slotsToProduce.flatMap(slot => slot.previewBookingIds ?? [])),
  ];
  const directions = [...new Set(slotsToProduce.map(slot => slot.direction))];
  const filterCourseSchedules = slotsToProduce.map(slot => ({
    courseId: slot.courseId,
    direction: slot.direction,
    shiftTime: slot.shiftTime,
  }));

  const shiftTimesMs = slotsToProduce
    .map(slot => dateToComparableNumber(slot.shiftTime))
    .filter(ms => ms > 0);

  const minShiftMs = shiftTimesMs.length
    ? Math.min(...shiftTimesMs)
    : Date.now();
  const maxShiftMs = shiftTimesMs.length
    ? Math.max(...shiftTimesMs)
    : Date.now();

  const criteria: NonNullable<IProduceJoinDto["criteria"]> = {
    bookTime: {
      $between: [convertUTCToISO(minShiftMs), convertUTCToISO(maxShiftMs)],
    },
    ...(directions.length === 1
      ? { direction: { $eq: directions[0] as DirectionEnum } }
      : { direction: { $in: directions as DirectionEnum[] } }),
  };

  if (bookingIds.length) {
    criteria.id = { $in: bookingIds };
  }

  const produceJoinDto: IProduceJoinDto = {
    action: BulkProduceTypeEnum.Produce,
    productionType: [ProductionTypeEnum.Shuttle, ProductionTypeEnum.FlexCourse],
    criteria,
    filterCourseIds: courseIds.length ? courseIds : undefined,
    filterCourseSchedules: filterCourseSchedules.length
      ? filterCourseSchedules
      : undefined,
  };
  return produceJoinDto;
};
